import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import styles from './A_SearchInput.module.css';
import cn from 'classnames';

interface A_SearchInputProps {
  placeholder?: string;
  /** Задан — режим controlled (значение с родителя) */
  value?: string;
  onSearch?: (query: string) => void;
  onChange?: (query: string) => void;
  autoFocus?: boolean;
  alwaysExpanded?: boolean;
  maxWidth?: string;
  zIndex?: number;
}

export default function A_SearchInput({
  placeholder,
  value: valueProp,
  onSearch,
  onChange,
  autoFocus = false,
  alwaysExpanded = false,
  maxWidth = '612px',
  zIndex = 1000,
}: A_SearchInputProps) {
  const isControlled = valueProp !== undefined;
  const [isExpanded, setIsExpanded] = useState(autoFocus || alwaysExpanded);
  const [innerQuery, setInnerQuery] = useState('');
  const searchQuery = isControlled ? valueProp : innerQuery;
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleSearchButtonClick = useCallback(() => {
    if ((alwaysExpanded || isExpanded) && onSearch && searchQuery.trim()) {
      onSearch(searchQuery.trim());
    } else if (!alwaysExpanded && !isExpanded) {
      setIsExpanded(true);
    }
  }, [alwaysExpanded, isExpanded, onSearch, searchQuery]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (!isControlled) {
        setInnerQuery(value);
      }
      onChange?.(value);
    },
    [isControlled, onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && onSearch && searchQuery.trim()) {
        onSearch(searchQuery.trim());
      } else if (e.key === 'Escape' && !alwaysExpanded) {
        setIsExpanded(false);
        if (!isControlled) {
          setInnerQuery('');
        }
        onChange?.('');
      }
    },
    [alwaysExpanded, isControlled, onChange, onSearch, searchQuery]
  );

  useEffect(() => {
    if (alwaysExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        if (isExpanded && !searchQuery.trim()) {
          setIsExpanded(false);
          if (!isControlled) {
            setInnerQuery('');
          }
          onChange?.('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [alwaysExpanded, isControlled, isExpanded, onChange, searchQuery]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      setIsExpanded(true);
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleInputBlur = useCallback(() => {
    if (alwaysExpanded) return;

    setTimeout(() => {
      if (
        inputRef.current &&
        !inputRef.current.contains(document.activeElement)
      ) {
        if (!searchQuery.trim()) {
          setIsExpanded(false);
        }
      }
    }, 100);
  }, [alwaysExpanded, searchQuery]);

  return (
    <div
      ref={containerRef}
      className={styles.wrapper}
      style={{ maxWidth, zIndex }}
    >
      <button
        onClick={handleSearchButtonClick}
        className={cn(styles.search_button, isExpanded && styles.expanded)}
        type='button'
        aria-label={isExpanded ? 'Выполнить поиск' : 'Открыть поиск'}
      >
        <Q_Icon.SearchIcon width={20} height={20} fill='none' />
      </button>
      <input
        ref={inputRef}
        type='text'
        placeholder={placeholder}
        value={searchQuery}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleInputBlur}
        className={cn(
          styles.search_input,
          'text-p-7',
          (isExpanded || alwaysExpanded) && styles.expanded
        )}
        style={{ maxWidth: isExpanded || alwaysExpanded ? maxWidth : '0' }}
      />
    </div>
  );
}
